/**
 * PostHog product-analytics integration.
 *
 * Autocapture records every click, page view, and form submission
 * without per-event instrumentation. Session replay records a video
 * of the user's actual session (with sensitive fields masked).
 *
 * Activates only when VITE_POSTHOG_KEY is set — in local dev with no
 * key, every exported function is a no-op so the app behaves
 * identically to pre-integration.
 *
 * Privacy:
 *  - All <input>, <textarea>, and <select> contents are masked by
 *    default (PostHog's maskAllInputs).
 *  - Elements with `class="ph-no-capture"` are masked in both
 *    autocapture and session replay. The masked set (T14 item 3):
 *      journal entries, DM bodies, the progress photo library,
 *      the whole sobriety surface (substance, day count, reset notes),
 *      the honesty check-in sheet and wizard, the mood dialog and Mood
 *      Tracker page (notes + mental-wellbeing score), bodyweight and
 *      body measurements, and the vision board.
 *    **If you add a screen where someone writes about what they are
 *    struggling with, it goes on this list in the same commit.** These
 *    are the surfaces the privacy one-pager is written against — a
 *    replay capturing a reset note makes that document false.
 *  - On logout we call posthog.reset() so the next session doesn't
 *    inherit the previous user's identity.
 *
 * Relationship to the existing analytics_events table:
 *  - PostHog covers the "everything" layer (automatic clicks, page
 *    views, session recordings, funnels).
 *  - Our backend analytics_events table stays for the deliberate,
 *    handcrafted events that need server-side correlation (onboarding
 *    completion, subscription transitions, etc.). Both run in parallel.
 */

import posthog from "posthog-js";
import { setSessionUrlProvider } from "@/lib/sentry";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://us.i.posthog.com";

let initialised = false;

/** Initialise PostHog once at app boot. Safe to call even when the
 *  key isn't set — becomes a no-op in that case. */
export function initPostHog(): void {
  if (initialised) return;
  if (!POSTHOG_KEY) {
    // No key → no-op. Matches dev / preview environments where we
    // don't want to pollute the real PostHog project with test data.
    return;
  }
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only", // create profile rows only for logged-in users
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        maskAllInputs: true,
        // CSS selector for extra elements to mask in session replays.
        // Masks the matching element AND its descendants' text, so the
        // class goes on the container, not on every leaf. See the
        // Privacy block at the top of this file for the current set.
        maskTextSelector: ".ph-no-capture, .ph-mask, [data-ph-mask='true']",
      },
      // Don't fire a page view on init — PageTrackingHandler in App.tsx
      // already handles that on every route change and would cause a
      // duplicate event here.
      loaded: () => {
        initialised = true;
        // ── PostHog ↔ Sentry link ─────────────────────────────────────────
        // Register a provider so lib/sentry.ts's `beforeSend` hook can
        // attach the current PostHog session replay URL to every Sentry
        // event. This makes a Sentry issue link to its PostHog replay and
        // vice-versa — no circular import (sentry.ts never imports posthog).
        setSessionUrlProvider(() => {
          try {
            return (
              posthog.get_session_replay_url?.({ withTimestamp: true }) ?? null
            );
          } catch {
            return null;
          }
        });
      },
    });
  } catch (err) {
    // Never throw — analytics failure must not break the app boot.
    // eslint-disable-next-line no-console
    console.warn("posthog init failed:", err);
  }
}

/** Tie the current browser session to a known user. Call on login. */
export function identifyUser(
  userId: string,
  traits: Record<string, unknown> = {},
): void {
  if (!POSTHOG_KEY) return;
  try {
    posthog.identify(userId, traits);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("posthog identify failed:", err);
  }
}

/** Clear the user association. Call on logout so the next user's
 *  actions don't get attributed to the previous one. */
export function resetPostHog(): void {
  if (!POSTHOG_KEY) return;
  try {
    posthog.reset();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("posthog reset failed:", err);
  }
}

/** Fire a custom event. Most of the time autocapture covers you;
 *  this is for deliberate events like 'subscription_upgraded' or
 *  'onboarding_step_complete' where you want a named event in PostHog
 *  for funnels. Existing analytics_events tracking stays in parallel. */
export function capturePostHog(event: string, props: Record<string, unknown> = {}): void {
  if (!POSTHOG_KEY) return;
  try {
    posthog.capture(event, props);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("posthog capture failed:", err);
  }
}
